import { Megaphone, Shield, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#06222A]">
      {/* Background gradients */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2E38] via-[#06222A] to-[#031419]" />
        <div className="absolute top-1/4 left-1/4 w-[800px] h-[800px] bg-[#32B4AC]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(50,180,172,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(50,180,172,0.6) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-md mx-4 flex flex-col items-center" data-testid="login-page">
        {/* Logo tile */}
        <div className="relative mb-7" data-testid="login-logo">
          <div className="absolute inset-0 bg-[#32B4AC] blur-2xl opacity-50 scale-90" />
          <div className="relative w-24 h-24 rounded-[22px] bg-gradient-to-br from-[#3DCFC5] to-[#1F8A82] shadow-2xl shadow-[#32B4AC]/40 flex items-center justify-center">
            <div className="w-16 h-16 rounded-xl bg-[#0A2E38]/60 backdrop-blur-sm flex items-center justify-center border border-white/10">
              <Megaphone className="w-9 h-9 text-[#3DCFC5]" strokeWidth={2.2} />
            </div>
          </div>
        </div>

        {/* Brand */}
        <h1 className="text-6xl font-bold text-white tracking-tight mb-1.5" style={{ letterSpacing: '0.04em' }}>
          TRACE
        </h1>
        <p className="text-xs text-[#3DCFC5]/80 tracking-[0.3em] uppercase mb-7">
          BY WELLVERSED<sup className="text-[8px]">™</sup>
        </p>

        {/* Tagline */}
        <p className="text-center text-gray-300 text-sm mb-10 max-w-sm leading-relaxed">
          Centralized Platform for{' '}
          <span className="text-[#3DCFC5] font-medium">Multi-Channel</span>
          {' / '}
          <span className="text-[#3DCFC5] font-medium">Brand</span> Paid Marketing Intelligence.
        </p>

        {/* Sign-in card */}
        <div className="w-full bg-[#0A2E38]/70 backdrop-blur-md border border-[#32B4AC]/15 rounded-2xl p-7 shadow-2xl">
          <h2 className="text-white text-center text-xl font-semibold mb-1.5">Welcome back</h2>
          <p className="text-gray-400 text-center text-sm mb-6">
            Sign in with your Wellversed Google account
          </p>

          <Button
            onClick={handleLogin}
            className="w-full bg-white hover:bg-gray-50 text-gray-900 font-medium py-6 text-base shadow-lg transition-all hover:shadow-xl rounded-xl flex items-center justify-center gap-3"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="mt-4 flex items-center justify-center gap-2 px-3 py-2 bg-[#032025] border border-[#32B4AC]/15 rounded-lg">
            <Shield className="w-3.5 h-3.5 text-[#3DCFC5]" />
            <span className="text-xs text-gray-300">
              Restricted to <span className="text-[#3DCFC5] font-medium">@wellversed.in</span> emails only
            </span>
          </div>

          <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-center gap-5 text-[10px] uppercase tracking-widest text-gray-500">
            <span className="flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> SSL Secured
            </span>
            <span className="w-px h-3 bg-gray-700" />
            <span className="flex items-center gap-1.5">
              <Shield className="w-3 h-3" /> Enterprise SSO
            </span>
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6 max-w-sm">
          By signing in, you agree to use TRACE with your Wellversed credentials.
        </p>
      </div>
    </div>
  );
}
